import React, { Component } from 'react'
import { observer, inject } from 'mobx-react'
import SearchWidget from '../../../SearchWidget'
import Form from '../settings/Form'
import PostLink from '../../../util/PostLink'
import SortMenu from '../../../util/SortMenu'
import MultiSelectMenu from "../../../multi_select"
import { getPeteErrorMessage } from '../../../../utils/pete_error_codes'

function processWildcard(wildcard, type) {
  if (!wildcard) return null
  const contains = s => `*${s}*`
  const starts = s => `${s}*`
  const filters = { contains, starts }
  const fields = Object.keys(wildcard)
  fields.forEach(field => {
    wildcard[field] = wildcard[field].map(filters[type])
  })
}

@inject('searches', 'websites')
@observer
export default class SearchForm extends Component {
  website = this.props.websites.current

  state = {
    filters: null,
    searchErrorCode: null,
    wildcardLocationType: 'subdivision',
    wildcardQueryType: 'starts',
    previewFormFilters: null,
    streamlined: true,
    agent_ids: this.props.search && this.props.search.params.agent_ids || [],
    office_ids: this.props.search && this.props.search.params.office_ids || [],
  }

  // Pete returns a 200 with an error code (e.g. REQUEST_URI_TOO_LONG) instead of
  // results. Such a search can never run, so block creating or updating it.
  searchErrorMessage() {
    return getPeteErrorMessage(this.state.searchErrorCode)
  }

  handleSearchErrorCode = searchErrorCode => {
    if (searchErrorCode !== this.state.searchErrorCode) {
      this.setState({ searchErrorCode })
    }
  }

  handleWildCardLocationTypeChange = event => {
    this.setState({ wildcardLocationType: event.target.value })
  }

  handleWildCardQueryTypeChange = event => {
    this.setState({ wildcardQueryType: event.target.value })
  }

  handleFilters = filters => this.setState({ filters })

  handleData = data => {
    const searchError = this.searchErrorMessage()
    if (searchError) {
      // Shaped like an axios error so `Form` lists it with the other problems.
      return Promise.reject({ response: { data: { errors: { base: [searchError] } } } })
    }
    const { sort_by, sort_dir, agent_ids, office_ids, wildcard, ...form } = data
    processWildcard(wildcard, this.state.wildcardQueryType)
    const params = { ...this.state.filters, sort_by, sort_dir, agent_ids, office_ids, wildcard }
    if (sort_by === 'map_center') {
      params.sort_by = null
    }
    const payload = { ...form, params }
    return this.props.onData(payload)
  }

  handleChange = () => {
    const { data } = this.form.process()
    const { sort_by, sort_dir, agent_ids, office_ids, wildcard } = data
    processWildcard(wildcard, this.state.wildcardQueryType)
    const previewFormFilters = { sort_by, sort_dir, agent_ids, office_ids, wildcard }
    this.setState({ previewFormFilters })
  }

  getPreviewLink() {
    const { filters, previewFormFilters } = this.state
    if (filters && !this.searchErrorMessage()) {
      const params = Object.assign({}, filters, previewFormFilters || {})
      const qrystr = JSON.stringify(params)
      return (
        <PostLink href={this.website.default_url} params={{initial_search_filters:qrystr}} target="_blank">Preview Results</PostLink>
      )
    }
  }

  warning() {
    const { search, duplicate } = this.props
    if (!search || duplicate) {
      return
    }
    const { hotsheet_count, recommended_count } = search
    if (hotsheet_count || recommended_count) {
      return (
        <div>
          Warning: Editing this search will effect {hotsheet_count} Hotsheets and {recommended_count} Recommended Searches.
        </div>
      )
    }
  }

  handleStreamlined = event => {
    event.preventDefault()
    this.setState({ streamlined: true })
  }

  handleAdvanced = event => {
    event.preventDefault()
    this.setState({ streamlined: false })
  }

  addAgent = agent => {
    const { agent_ids } = this.state
    this.setState({ agent_ids: [...agent_ids, agent] })
  }

  removeAgent = agent => {
    const { agent_ids } = this.state
    this.setState({ agent_ids: agent_ids.filter(id => id !== agent) })
  }

  addOffice = office => {
    const { office_ids } = this.state
    this.setState({ office_ids: [...office_ids, office] })
  }

  removeOffice = office => {
    const { office_ids } = this.state
    this.setState({ office_ids: office_ids.filter(id => id !== office) })
  }

  render() {
    const { search, duplicate } = this.props
    const { streamlined } = this.state
    const searchError = this.searchErrorMessage()

    const action = duplicate ? 'Duplicate' : search ? 'Update' : 'Save'

    let defaultWildcardLocationType = search && search.params.wildcard && Object.keys(search.params.wildcard)[0]
    let defaultWildcardQueryType = 'starts'
    let defaultWildcardValue = search && search.params.wildcard && search.params.wildcard[defaultWildcardLocationType][0]

    if (defaultWildcardValue && defaultWildcardValue.match(/^\*/)) {
      defaultWildcardQueryType = 'contains'
    }
    if (defaultWildcardValue) {
      defaultWildcardValue = defaultWildcardValue.replace(/^\*|\*$/g, '')
    }

    return (
      <div className="container centered padded website-page">
        <Form ref={el => this.form = el} className="full" onData={this.handleData} onChange={this.handleChange} validations={this.props.validations}>
          {/* <input type="hidden" name="email_frequency" value="never" /> */}

          <div className="panel padded">
            {search ? (
              <span>
                <input type="hidden" name="search_id" value={search.id} />
                <h2 className="title">{action} Search: {search.title}</h2>
              </span>
            ) : (
              <span>
                <h2 className="title">New Search</h2>
              </span>
            )}

            {this.warning()}

            <div className="form-section">
              <label htmlFor="title">Search Name</label>
              <input name="title" id="title" placeholder="Enter a Name" defaultValue={search && search.title} />
            </div>

            <div className="form-section">
              <label htmlFor="title">Select Search Form</label>
              <div className="search_ui_toggle">
                <div onClick={this.handleStreamlined}>
                  <h3>
                    <input type="radio" name="search_ui" value="streamlined" checked={streamlined} />
                    Streamlined Search Form
                  </h3>
                  <ul>
                    <li>Ideal For Searching Multiple MLS's</li>
                  </ul>
                </div>
                <div onClick={this.handleAdvanced}>
                  <h3>
                    <input type="radio" name="search_ui" value="advanced" checked={!streamlined} />
                    Advanced Search Form
                  </h3>
                  <ul>
                    <li>Detailed Advanced Features</li>
                  </ul>
                </div>
              </div>
              <hr />
            </div>

            <div className="form-section">
              <label htmlFor="title">Search Filters</label>
              <p className="muted">The search will use the configuration provided below. {this.getPreviewLink()}</p>
              {searchError && <p className="error">{searchError}</p>}
              <SearchWidget
                advanced={!streamlined} streamlined={streamlined}
                website_uuid={this.props.websites.current.uuid}
                params={search && search.params}
                onChange={this.handleFilters}
                onErrorCode={this.handleSearchErrorCode} />
            </div>

            <div className="form-section">
              <label>Wildcard</label>
              <p className="muted">Applies a wildcard search on locations. (<strong>case sensitive</strong>)</p>
              Type:
              <select style={{marginLeft: 5, marginRight: 15}} onChange={this.handleWildCardLocationTypeChange} defaultValue={defaultWildcardLocationType}>
                <option value="subdivision">Subdivision</option>
                <option value="building">Building</option>
              </select>
              <select style={{marginLeft: 5, marginRight: 15}} onChange={this.handleWildCardQueryTypeChange} defaultValue={defaultWildcardQueryType}>
                <option value="starts">Starts With</option>
                <option value="contains">Contains</option>
              </select>
              Value:
              <input
                name={`wildcard[${this.state.wildcardLocationType}][]`}
                defaultValue={defaultWildcardValue}
                style={{marginLeft: 5, width: '150px', display: 'inline-block'}} />
            </div>

            <div className="form-section">
              <label>Results Sorting</label>
              <p className="muted">This will re-order the results on hotsheets and recommendations.</p>
              Sort By:
              <SortMenu sortBy={search && search.params.sort_by} sortDir={search && search.params.sort_dir} saleType={this.state.filters && this.state.filters.sale_type} />
            </div>

            <div className="form-section">
              <label>Agent/Office Filtering</label>
                <div className='sidx-streamlined-search'>
                  <div className="sidx-streamlined-filter">
                      <div className="sidx-title">Agents</div>
                      <MultiSelectMenu
                        noneLabel="Choose an agent"
                        options={this.website.agent_ids}
                        selected={this.state.agent_ids}
                        onAdd={this.addAgent}
                        onRemove={this.removeAgent} />
                         {this.state.agent_ids.map(agent_id => (
                        <input key={agent_id} type="hidden" name="agent_ids[]" value={agent_id} />
                        ))}
                    </div>
                  </div>
                <div className='sidx-streamlined-search'>
                  <div className="sidx-streamlined-filter">
                      <div className="sidx-title">Offices</div>
                      <MultiSelectMenu
                        noneLabel="Choose an office"
                        options={this.website.office_ids}
                        selected={this.state.office_ids}
                        onAdd={this.addOffice}
                        onRemove={this.removeOffice} />
                         {this.state.office_ids.map(office_id => (
                        <input key={office_id} type="hidden" name="office_ids[]" value={office_id} />
                        ))}
                  </div>
                </div>
            </div>
          </div>
          <button disabled={!this.state.filters || !!searchError} type="submit" className="big shadowed">{action} Search</button>
        </Form>
      </div>
    )
  }
}



// WEBPACK FOOTER //
// client/components/sections/website/SearchLibrary/Form.js